import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Store as StoreIcon } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { CATEGORY_META, iconByName } from "../lib/categories";

export default function CategoryLanding() {
  const { name } = useParams();
  const cat = decodeURIComponent(name || "");
  const meta = CATEGORY_META.find((c) => c.name === cat);
  const [products, setProducts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [managed, setManaged] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/categories/manage").then((r) => { const m = r.data.find((c) => c.name === cat); setSubs(m?.subcategories || []); setManaged(m || null); }).catch(() => {});
    api.get("/products", { params: { category: cat } }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [cat]);

  const brands = [...new Set(products.map((p) => p.source_store || p.brand).filter(Boolean))].slice(0, 8);
  const featured = [...products].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)).slice(0, 20);
  const img = managed?.image || meta?.img;
  const blurb = managed?.blurb || meta?.blurb;
  const Icon = iconByName(managed?.icon) || meta?.Icon;

  return (
    <div data-testid="category-landing">
      <div className="relative overflow-hidden bg-[#0D530E] text-[#FBF5DD]">
        {img && <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />}
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#E7E1B1] mb-3">AfriGate · United Kingdom</p>
          {Icon && <span className="inline-flex w-14 h-14 rounded-2xl bg-[#E7E1B1]/15 items-center justify-center mb-4"><Icon size={26} /></span>}
          <h1 className="font-display text-5xl sm:text-6xl font-light tracking-tight mb-3" data-testid="cat-title">{cat}</h1>
          <p className="text-[#FBF5DD]/85 max-w-xl">{blurb || "Shop authentic UK products, delivered to Ghana with fully transparent pricing."}</p>
          <Link to={`/shop?category=${encodeURIComponent(cat)}`} data-testid="cat-shop-all" className="inline-flex items-center gap-2 mt-6 bg-[#E7E1B1] text-[#0D530E] font-semibold px-6 py-3 rounded-full hover:bg-white" style={{ transition: "background-color 0.2s ease" }}>Shop all {products.length} products <ArrowRight size={16} /></Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {subs.length > 0 && (
          <div className="mb-8">
            <h2 className="font-display text-2xl text-[#0D530E] mb-3">Browse {cat}</h2>
            <div className="flex gap-2 flex-wrap">
              {subs.map((s) => (
                <Link key={s} to={`/shop?category=${encodeURIComponent(cat)}&subcategory=${encodeURIComponent(s)}`} data-testid={`cat-sub-${s}`}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-white border border-[#0D530E]/12 text-[#0D530E] hover:border-[#306D29]" style={{ transition: "border-color 0.2s ease" }}>{s}</Link>
              ))}
            </div>
          </div>
        )}

        {brands.length > 0 && (
          <div className="mb-8">
            <h2 className="font-display text-2xl text-[#0D530E] mb-3">Top stores & brands</h2>
            <div className="flex gap-2 flex-wrap">
              {brands.map((b) => (
                <Link key={b} to={`/shop?category=${encodeURIComponent(cat)}&store=${encodeURIComponent(b)}`} data-testid={`cat-brand-${b}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-[#E7E1B1]/50 text-[#0D530E] hover:bg-[#E7E1B1]" style={{ transition: "background-color 0.2s ease" }}><StoreIcon size={14} /> {b}</Link>
              ))}
            </div>
          </div>
        )}

        <h2 className="font-display text-2xl text-[#0D530E] mb-4">Featured in {cat}</h2>
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5">{[...Array(10)].map((_, i) => <div key={i} className="aspect-square rounded-xl bg-[#E7E1B1]/50 animate-pulse" />)}</div>
        ) : products.length === 0 ? (
          <p className="text-[#306D29]/70 py-12" data-testid="cat-empty">No products in this category yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5" data-testid="cat-grid">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
